import React, { useEffect, useState, useRef, lazy, memo, Component } from 'react';
import { Calendar, Heart, Music, Sun, Navigation, ExternalLink, Camera, X, Volume2, VolumeX } from 'lucide-react';
// --- Optimized Components ---
const CustomCursor = memo(() => {
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const [trails, setTrails] = useState<Array<{
    x: number;
    y: number;
    id: number;
  }>>([]);
  const [isClicking, setIsClicking] = useState(false);
  useEffect(() => {
    let trailId = 0;
    let rafId: number;
    const handleMouseMove = (e: MouseEvent) => {
      rafId = requestAnimationFrame(() => {
        setPosition({
          x: e.clientX,
          y: e.clientY
        });
        const newTrail = {
          x: e.clientX,
          y: e.clientY,
          id: trailId++
        };
        setTrails(prev => [...prev.slice(-6), newTrail]); // Reduced from 8 to 6
      });
    };
    const handleMouseDown = () => setIsClicking(true);
    const handleMouseUp = () => setIsClicking(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);
  return <>
      {trails.map((trail, index) => <div key={trail.id} className="fixed pointer-events-none z-[9999] rounded-full bg-gradient-to-br from-orange-400 to-pink-500" style={{
      left: trail.x - 4,
      top: trail.y - 4,
      width: '8px',
      height: '8px',
      opacity: (index + 1) / trails.length * 0.6,
      transform: `scale(${(index + 1) / trails.length})`,
      transition: 'all 0.3s ease-out',
      boxShadow: '0 0 10px rgba(255, 140, 0, 0.5)'
    }} data-id="element-0" />)}

      <div className="fixed pointer-events-none z-[9999] rounded-full border-2 border-orange-400/60 transition-all duration-200 ease-out" style={{
      left: position.x - 20,
      top: position.y - 20,
      width: isClicking ? '30px' : '40px',
      height: isClicking ? '30px' : '40px',
      boxShadow: '0 0 20px rgba(255, 140, 0, 0.4)'
    }} data-id="element-1" />

      <div className="fixed pointer-events-none z-[9999] rounded-full bg-gradient-to-br from-orange-300 to-pink-400 transition-all duration-100 ease-out" style={{
      left: position.x - 6,
      top: position.y - 6,
      width: isClicking ? '16px' : '12px',
      height: isClicking ? '16px' : '12px',
      boxShadow: '0 0 15px rgba(255, 140, 0, 0.8), 0 0 30px rgba(255, 140, 0, 0.4)'
    }} data-id="element-2" />

      <div className="fixed pointer-events-none z-[9998] rounded-full bg-orange-400/20 blur-xl transition-all duration-300 ease-out" style={{
      left: position.x - 30,
      top: position.y - 30,
      width: '60px',
      height: '60px',
      opacity: isClicking ? 0.8 : 0.4
    }} data-id="element-3" />
    </>;
});
CustomCursor.displayName = 'CustomCursor';
// Background Music Player Component
const BackgroundMusic = () => {
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const toggleMute = () => {
    setIsMuted(!isMuted);
  };
  const handleUserInteraction = () => {
    if (!isPlaying) {
      setIsPlaying(true);
    }
  };
  useEffect(() => {
    // Add event listeners for user interaction to start music
    const events = ['click', 'touchstart', 'keydown'];
    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction, {
        once: true
      });
    });
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserInteraction);
      });
    };
  }, []);
  return <>
      {/* Hidden YouTube Player for Background Music */}
      {isPlaying && <iframe ref={iframeRef} style={{
      display: 'none'
    }} src={`https://www.youtube.com/embed/Fa8AXcfLXs4?autoplay=1&loop=1&playlist=Fa8AXcfLXs4&controls=0&mute=${isMuted ? 1 : 0}`} allow="autoplay; encrypted-media" title="Background Music" data-id="element-4" />}

      {/* Music Control Button */}
      <button onClick={toggleMute} className="fixed bottom-8 right-8 z-[9999] w-14 h-14 bg-gradient-to-br from-orange-400 to-pink-500 hover:from-orange-500 hover:to-pink-600 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110 border-2 border-white/20 backdrop-blur-sm" aria-label={isMuted ? 'Unmute music' : 'Mute music'} data-id="element-5">
        {isMuted ? <VolumeX className="w-6 h-6 text-white" data-id="element-6" /> : <Volume2 className="w-6 h-6 text-white" data-id="element-7" />}
      </button>

      {/* Music indicator */}
      {isPlaying && !isMuted && <div className="fixed bottom-24 right-8 z-[9999] bg-black/50 backdrop-blur-sm text-white text-xs px-3 py-2 rounded-full border border-white/20" data-id="element-8">
          🎵 Playing
        </div>}
    </>;
};
// Scroll reveal hook
const useScrollReveal = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);
  return {
    ref,
    isVisible
  };
};
const Lantern = memo(({
  delay,
  duration,
  left,
  size,
  opacity
}: {
  delay: number;
  duration: number;
  left: number;
  size: number;
  opacity: number;
}) => {
  return <div className="absolute lantern-float z-0 pointer-events-none" style={{
    left: `${left}%`,
    animationDuration: `${duration}s`,
    animationDelay: `${delay}s`,
    width: `${size}px`,
    height: `${size * 1.4}px`,
    opacity: opacity,
    bottom: '-150px'
  }} data-id="element-9">
        <div className="absolute inset-0 blur-2xl bg-gradient-to-b from-orange-400 via-pink-400 to-orange-500 opacity-70 scale-150" data-id="element-10"></div>

        <div className="w-full h-full rounded-t-lg rounded-b-md relative overflow-hidden shadow-[0_0_60px_rgba(255,140,0,0.9)]" data-id="element-11">
          <div className="absolute inset-0 bg-gradient-to-b from-orange-300 via-pink-400 to-orange-400" data-id="element-12"></div>
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4/5 h-4/5 bg-yellow-100 blur-2xl opacity-90 rounded-full" data-id="element-13"></div>
          <div className="absolute top-0 left-0 w-full h-1/3 bg-gradient-to-b from-white/40 to-transparent" data-id="element-14"></div>
          <div className="absolute top-0 left-0 w-full h-full border-t-2 border-orange-800/30" data-id="element-15"></div>
          <div className="absolute bottom-0 left-0 w-full h-2 bg-gradient-to-b from-orange-800/40 to-orange-900/60" data-id="element-16"></div>
        </div>
      </div>;
});
Lantern.displayName = 'Lantern';
const CountdownTimer = memo(({
  targetDate
}: {
  targetDate: Date;
}) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();
      if (difference <= 0) {
        return {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        };
      }
      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor(difference / (1000 * 60 * 60) % 24),
        minutes: Math.floor(difference / 1000 / 60 % 60),
        seconds: Math.floor(difference / 1000 % 60)
      };
    };
    setTimeLeft(calculateTimeLeft());
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);
  return <div className="grid grid-cols-4 gap-4 md:gap-8 max-w-3xl mx-auto mt-12 text-white" data-id="element-17">
      {[{
      label: 'Days',
      value: timeLeft.days
    }, {
      label: 'Hours',
      value: timeLeft.hours
    }, {
      label: 'Minutes',
      value: timeLeft.minutes
    }, {
      label: 'Seconds',
      value: timeLeft.seconds
    }].map((item, idx) => <div key={idx} className="flex flex-col items-center animate-scale-in" style={{
      animationDelay: `${idx * 100}ms`
    }} data-id="element-18">
          <div className="w-16 h-16 md:w-24 md:h-24 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center mb-2 shadow-lg hover:scale-110 transition-transform duration-300" data-id="element-19">
            <span className="text-2xl md:text-4xl font-serif font-bold" data-id="element-20">
              {item.value}
            </span>
          </div>
          <span className="text-xs md:text-sm uppercase tracking-widest font-light" data-id="element-21">
            {item.label}
          </span>
        </div>)}
    </div>;
});
CountdownTimer.displayName = 'CountdownTimer';
const EventCard = memo(({
  title,
  date,
  icon: Icon,
  delay
}: {
  title: string;
  date: string;
  icon: any;
  delay: number;
}) => <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-xl text-center transform hover:scale-105 hover:bg-white/15 transition-all duration-300 shadow-xl scroll-reveal" style={{
  animationDelay: `${delay}ms`
}} data-id="element-22">
      <div className="w-12 h-12 mx-auto bg-gradient-to-br from-orange-400 to-pink-500 rounded-full flex items-center justify-center mb-4 shadow-lg hover:rotate-12 transition-transform duration-300" data-id="element-23">
        <Icon className="w-6 h-6 text-white" data-id="element-24" />
      </div>
      <h3 className="text-xl md:text-2xl font-serif font-bold text-white mb-2" data-id="element-25">
        {title}
      </h3>
      <p className="text-orange-100 font-light tracking-wide" data-id="element-26">{date}</p>
    </div>);
EventCard.displayName = 'EventCard';
const VenueCard = memo(({
  title,
  address,
  mapUrl
}: {
  title: string;
  address: string;
  mapUrl: string;
}) => <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-all duration-300 scroll-reveal p-6 md:p-8" data-id="element-27">
      <div className="flex items-start gap-4 mb-4" data-id="element-28">
        <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg" data-id="element-29">
          <Navigation className="w-6 h-6 text-white" data-id="element-30" />
        </div>
        <div className="flex-1" data-id="element-31">
          <h3 className="text-2xl md:text-3xl font-serif font-bold text-white mb-2" data-id="element-32">
            {title}
          </h3>
          <p className="text-orange-100 font-light leading-relaxed" data-id="element-33">
            {address}
          </p>
        </div>
      </div>

      <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="w-full mt-4 flex items-center justify-center gap-2 bg-gradient-to-r from-orange-400 to-pink-500 hover:from-orange-500 hover:to-pink-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5" data-id="element-34">
        <Navigation className="w-5 h-5" data-id="element-35" />
        <span data-id="element-36">Get Directions</span>
        <ExternalLink className="w-4 h-4" data-id="element-37" />
      </a>
    </div>);
VenueCard.displayName = 'VenueCard';
const PhotoGallery = memo(() => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const {
    ref,
    isVisible
  } = useScrollReveal();
  const photos = [{
    id: 1,
    url: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=800&q=80',
    caption: 'Our Special Moment'
  }, {
    id: 2,
    url: 'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=800&q=80',
    caption: 'Together Forever'
  }, {
    id: 3,
    url: 'https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=800&q=80',
    caption: 'Love Story'
  }, {
    id: 4,
    url: 'https://images.unsplash.com/photo-1591604466107-ec97de577aff?w=800&q=80',
    caption: 'Beautiful Day'
  }, {
    id: 5,
    url: 'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=800&q=80',
    caption: 'Precious Memories'
  }, {
    id: 6,
    url: 'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=800&q=80',
    caption: 'Celebration'
  }];
  return <div ref={ref} className={`scroll-reveal ${isVisible ? 'revealed' : ''}`} data-id="element-38">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" data-id="element-39">
        {photos.map((photo, index) => <div key={photo.id} className="gallery-item aspect-square rounded-xl cursor-pointer shadow-xl" style={{
        animationDelay: `${index * 100}ms`
      }} onClick={() => setSelectedImage(index)} data-id="element-40">
            <img src={photo.url} alt={photo.caption} className="w-full h-full object-cover rounded-xl" loading="lazy" data-id="element-41" />
            <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300 z-10" data-id="element-42">
              <Camera className="w-8 h-8 text-white drop-shadow-lg" data-id="element-43" />
            </div>
          </div>)}
      </div>

      {selectedImage !== null && <div className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4 animate-fade-in" onClick={() => setSelectedImage(null)} data-id="element-44">
          <button className="absolute top-4 right-4 text-white hover:text-orange-400 transition-colors" onClick={() => setSelectedImage(null)} data-id="element-45">
            <X className="w-8 h-8" data-id="element-46" />
          </button>
          <div className="max-w-4xl w-full animate-scale-in" data-id="element-47">
            <img src={photos[selectedImage].url} alt={photos[selectedImage].caption} className="w-full h-auto rounded-lg shadow-2xl" data-id="element-48" />
            <p className="text-white text-center mt-4 text-lg font-light" data-id="element-49">
              {photos[selectedImage].caption}
            </p>
          </div>
        </div>}

      <div className="mt-8 text-center" data-id="element-50">
        <p className="text-orange-200/80 text-sm font-light italic" data-id="element-51">
          📸 Replace placeholder images with your beautiful couple photos
        </p>
      </div>
    </div>;
});
PhotoGallery.displayName = 'PhotoGallery';
export function App() {
  // Reduced lanterns from 45 to 30 for better performance
  const lanterns = Array.from({
    length: 30
  }).map((_, i) => ({
    id: i,
    delay: Math.random() * 25,
    duration: 12 + Math.random() * 18,
    left: Math.random() * 100,
    size: 40 + Math.random() * 70,
    opacity: 0.75 + Math.random() * 0.25
  }));
  const marriageDate = new Date('2026-04-21T00:00:00');
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    }, {
      threshold: 0.1
    });
    const elements = document.querySelectorAll('.scroll-reveal');
    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return <div className="min-h-screen w-full relative overflow-hidden font-sans" data-id="element-52">
      <CustomCursor data-id="element-53" />
      <BackgroundMusic data-id="element-54" />

      <div className="fixed inset-0 z-[-2] bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: 'url("https://images.unsplash.com/photo-1510414842594-a61c69b5ae57?q=80&w=2070&auto=format&fit=crop")'
    }} data-id="element-55" />

      <div className="fixed inset-0 z-[-1] bg-gradient-to-b from-orange-500/40 via-pink-500/40 to-blue-900/70 mix-blend-multiply" data-id="element-56" />
      <div className="fixed inset-0 z-[-1] bg-gradient-to-t from-blue-900/80 via-transparent to-transparent" data-id="element-57" />

      <div className="fixed inset-0 overflow-hidden pointer-events-none z-10" data-id="element-58">
        {lanterns.map(l => <Lantern key={l.id} {...l} data-id="element-59" />)}
      </div>

      <main className="relative z-20 w-full min-h-screen flex flex-col items-center justify-start overflow-y-auto overflow-x-hidden" data-id="element-60">
        <section className="min-h-screen w-full flex flex-col items-center justify-center px-4 py-20 text-center" data-id="element-61">
          <div className="space-y-8" data-id="element-62">
            <p className="text-orange-100 uppercase tracking-[0.3em] text-sm md:text-base mb-4 font-light drop-shadow-md animate-fade-in" data-id="element-63">
              We Are Getting Married
            </p>

            <div className="space-y-2 md:space-y-6" data-id="element-64">
              <h1 className="text-5xl md:text-8xl font-serif text-white drop-shadow-lg leading-tight animate-slide-in-left" data-id="element-65">
                Shivam
              </h1>
              <div className="flex items-center justify-center gap-4 text-orange-200 animate-fade-in" style={{
              animationDelay: '200ms'
            }} data-id="element-66">
                <div className="h-[1px] w-12 bg-orange-200/60" data-id="element-67"></div>
                <span className="font-serif italic text-2xl md:text-3xl" data-id="element-68">
                  weds
                </span>
                <div className="h-[1px] w-12 bg-orange-200/60" data-id="element-69"></div>
              </div>
              <h1 className="text-5xl md:text-8xl font-serif text-white drop-shadow-lg leading-tight animate-slide-in-right" style={{
              animationDelay: '300ms'
            }} data-id="element-70">
                Chaitali
              </h1>
            </div>

            <div className="mt-12 space-y-8 text-white/90 max-w-4xl mx-auto backdrop-blur-sm bg-black/10 p-8 rounded-2xl border border-white/10 animate-scale-in" style={{
            animationDelay: '500ms'
          }} data-id="element-71">
              <div className="grid md:grid-cols-2 gap-8 md:gap-16" data-id="element-72">
                <div className="space-y-2" data-id="element-73">
                  <p className="text-xs uppercase tracking-widest text-orange-200 mb-2" data-id="element-74">
                    Groom
                  </p>
                  <h3 className="text-xl font-serif font-semibold" data-id="element-75">
                    Shivam Kolhe
                  </h3>
                  <p className="text-sm font-light opacity-90" data-id="element-76">
                    Son of Dr. Vikas Kolhe & Mrs. Leena Kolhe
                  </p>
                </div>
                <div className="space-y-2" data-id="element-77">
                  <p className="text-xs uppercase tracking-widest text-orange-200 mb-2" data-id="element-78">
                    Bride
                  </p>
                  <h3 className="text-xl font-serif font-semibold" data-id="element-79">
                    Chaitali Chaudhari
                  </h3>
                  <p className="text-sm font-light opacity-90" data-id="element-80">
                    Daughter of Mr. Nilesh Chaudhari & Mrs. Charulata Chaudhari
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-12 animate-fade-in-up" style={{
            animationDelay: '700ms'
          }} data-id="element-81">
              <p className="text-white text-lg md:text-xl font-serif italic mb-6" data-id="element-82">
                Countdown to the Big Day
              </p>
              <CountdownTimer targetDate={marriageDate} data-id="element-83" />
            </div>
          </div>

          <div className="absolute bottom-8 animate-bounce" data-id="element-84">
            <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2" data-id="element-85">
              <div className="w-1 h-2 bg-white rounded-full" data-id="element-86"></div>
            </div>
          </div>
        </section>

        <section className="w-full max-w-6xl px-4 py-24 md:py-32" data-id="element-87">
          <div className="text-center mb-16 scroll-reveal" data-id="element-88">
            <h2 className="text-4xl md:text-5xl font-serif text-white mb-4 drop-shadow-md" data-id="element-89">
              Wedding Events
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-orange-400 to-transparent mx-auto" data-id="element-90"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-91">
            <EventCard title="Engagement" date="22 February 2026" icon={Heart} delay={0} data-id="element-92" />
            <EventCard title="Haldi" date="20 April 2026" icon={Sun} delay={100} data-id="element-93" />
            <EventCard title="Sangeet" date="20 April 2026" icon={Music} delay={200} data-id="element-94" />
            <EventCard title="Marriage" date="21 April 2026" icon={Calendar} delay={300} data-id="element-95" />
          </div>
        </section>

        <section className="w-full max-w-6xl px-4 py-24 md:py-32" data-id="element-96">
          <div className="text-center mb-16 scroll-reveal" data-id="element-97">
            <h2 className="text-4xl md:text-5xl font-serif text-white mb-4 drop-shadow-md" data-id="element-98">
              Our Journey
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-orange-400 to-transparent mx-auto mb-4" data-id="element-99"></div>
            <p className="text-orange-100 font-light text-lg" data-id="element-100">
              Capturing beautiful moments together
            </p>
          </div>

          <PhotoGallery data-id="element-101" />
        </section>

        <section className="w-full max-w-6xl px-4 py-24 md:py-32" data-id="element-102">
          <div className="text-center mb-16 scroll-reveal" data-id="element-103">
            <h2 className="text-4xl md:text-5xl font-serif text-white mb-4 drop-shadow-md" data-id="element-104">
              Venue Details
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-orange-400 to-transparent mx-auto mb-4" data-id="element-105"></div>
            <p className="text-orange-100 font-light text-lg" data-id="element-106">
              Join us at these beautiful locations
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-107">
            <VenueCard title="Engagement Ceremony" address="Please update with your engagement venue address" mapUrl="https://www.google.com/maps/search/?api=1&query=Mumbai+India" data-id="element-108" />

            <VenueCard title="Marriage Ceremony" address="Please update with your marriage venue address" mapUrl="https://www.google.com/maps/search/?api=1&query=Mumbai+India" data-id="element-109" />
          </div>

          <div className="mt-8 text-center" data-id="element-110">
            <p className="text-orange-200/80 text-sm font-light italic" data-id="element-111">
              💡 Tip: Update the venue addresses and map URLs in the code to
              show your actual locations
            </p>
          </div>
        </section>

        <footer className="w-full py-12 text-center text-white/60 text-sm font-light scroll-reveal" data-id="element-112">
          <p className="mb-2" data-id="element-113">We can't wait to celebrate with you!</p>
          <p data-id="element-114">© 2026 Shivam & Chaitali</p>
        </footer>
      </main>
    </div>;
}